// TarjetasCursoSection.js — Sección `tarjetas_curso`: tarjetas blancas con
// imagen de portada, título, descripción y un botón («Desarrollo profesional»
// de LAAC Academy, docs/Lac/desarollo.png).
//
// Cada tarjeta: { imagen, imagen_alt, titulo, texto, boton_texto,
// accion: 'url' | 'login' | 'registro' | 'solicitud' | 'ninguna', url,
// nueva_pestana }. El botón «Acceder» abre el inicio de sesión del proyecto.
import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { BACKEND_BASE } from '../../../services/api';
import { crearActivador } from './accionesBoton';
import { useAuth } from '../../../contexts/AuthContext';

const urlImagen = (v) => (!v ? null : (/^https?:\/\//.test(v) || v.startsWith('/') ? v : `${BACKEND_BASE}/${v}`));

const subir = keyframes`from { opacity: 0; transform: translate3d(0, 30px, 0); } to { opacity: 1; transform: none; }`;

const Seccion = styled.section`
  background: ${p => p.$fondo};
  padding: ${p => p.$padding};
  font-family: Arial, Helvetica, sans-serif;
  @media (max-width: 980px) { padding: ${p => p.$paddingMovil}; }
`;

const Fila = styled.div`
  width: 90%;
  max-width: ${p => p.$ancho};
  margin: 0 auto;
`;

const Encabezado = styled.div`
  text-align: ${p => p.$align};
  margin-bottom: 34px;
  h2 { margin: 0 0 8px; padding: 0; font-family: 'Roboto', Helvetica, Arial, sans-serif; font-size: 34px; font-weight: 700; line-height: 1.1; color: ${p => p.$color}; }
  p { margin: 0; color: #808080; font-size: 17px; line-height: 1.4; }
  @media (max-width: 980px) { text-align: center; h2 { font-size: 24px; } }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${p => p.$columnas}, minmax(0, 1fr));
  gap: ${p => p.$gap};
  @media (max-width: 980px) { grid-template-columns: 1fr; gap: 28px; }
`;

const Tarjeta = styled.article`
  background: ${p => p.$fondo};
  border-radius: ${p => p.$radio};
  box-shadow: ${p => p.$sombra};
  padding: ${p => p.$padding};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  transition: box-shadow .3s ease, transform .3s ease;
  ${p => p.$animar && css`
    animation: ${subir} 700ms ease both;
    animation-delay: ${p.$retraso}ms;
    @media (prefers-reduced-motion: reduce) { animation: none; }
  `}
  &:hover { transform: translateY(-4px); box-shadow: 0 25px 50px -12px rgb(26 10 46 / .25); }
  @media (max-width: 980px) { padding: 24px 20px 30px; }
`;

const Portada = styled.div`
  width: 100%;
  max-width: ${p => p.$ancho};
  margin: 0 auto 24px;
  overflow: hidden;
  border-radius: ${p => p.$radio};
  img { display: block; width: 100%; height: auto; }
`;

const Titulo = styled.h3`
  margin: 0 0 12px;
  padding: 0;
  font-family: 'Roboto', Helvetica, Arial, sans-serif;
  font-size: ${p => p.$size};
  font-weight: 700;
  line-height: 1.25;
  color: ${p => p.$color};
`;

const Texto = styled.div`
  margin: 0;
  color: ${p => p.$color};
  font-size: 15px;
  line-height: 1.5;
  p { margin: 0 0 10px; }
  p:last-child { margin-bottom: 0; }
`;

const Pie = styled.div`
  margin-top: auto;
  padding-top: 26px;
  display: flex;
  justify-content: ${p => p.$align};
`;

const Boton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 13px 34px;
  border: none;
  border-radius: 11px;
  background: ${p => p.$fondo};
  color: ${p => p.$color};
  font-family: inherit;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: .3px;
  cursor: pointer;
  transition: transform .3s ease, box-shadow .3s ease;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 22px rgb(168 85 247 / .35); }
`;

const TarjetasCursoSection = ({ config = {}, openLogin, openRegister, tl = (v, d = '') => v || d }) => {
  const c = config;
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const activarBase = crearActivador({ navigate, openLogin, openRegister });
  // `url_con_sesion`: con sesión el botón lleva directo ahí (p. ej. al curso);
  // sin sesión inicia sesión y, al entrar, lleva a esa misma dirección.
  const activar = (it) => {
    if (it.url_con_sesion && user) return navigate(it.url_con_sesion);
    if (it.url_con_sesion && (it.accion || 'login') === 'login' && openLogin) return openLogin({ destino: it.url_con_sesion });
    return activarBase(it);
  };
  const items = Array.isArray(c.items) ? c.items.filter(it => it && (it.titulo || it.texto || it.imagen)) : [];
  if (!items.length) return null;

  const titulo = tl(c.titulo, '');
  const subtitulo = tl(c.subtitulo, '');
  const columnas = Math.min(Number(c.columnas) || Math.min(items.length, 3), 4);

  return (
    <Seccion
      $fondo={c.fondo || '#faf7ff'}
      $padding={c.padding || '70px 0 80px'}
      $paddingMovil={c.padding_movil || '45px 0 55px'}
    >
      <Fila $ancho={c.ancho_maximo || (columnas === 1 ? '700px' : '1200px')}>
        {(titulo || subtitulo) && (
          <Encabezado $align={c.alineacion_titulo || 'center'} $color={c.color_titulo || '#1a0a2e'}>
            {titulo && <h2>{titulo}</h2>}
            {subtitulo && <p>{subtitulo}</p>}
          </Encabezado>
        )}
        <Grid $columnas={columnas} $gap={c.separacion || '30px'}>
          {items.map((it, i) => {
            const img = urlImagen(it.imagen);
            // Con sesión el botón puede decir otra cosa («Ingresar al aula» en vez de «Acceder»).
            const textoBoton = (user && tl(it.boton_texto_con_sesion, '')) || tl(it.boton_texto, '');
            return (
              <Tarjeta
                key={it.id || i}
                $fondo={c.color_tarjeta || '#ffffff'}
                $radio={c.radio_tarjeta || '20px'}
                $sombra={c.sombra_tarjeta || '0 10px 40px rgb(26 10 46 / .06)'}
                $padding={c.padding_tarjeta || '38px 38px 34px'}
                $animar={c.animar !== false}
                $retraso={i * 120}
              >
                {img && (
                  <Portada $ancho={c.ancho_imagen || '500px'} $radio={c.radio_imagen || '8px'}>
                    <img src={img} alt={tl(it.imagen_alt, '')} loading="lazy" />
                  </Portada>
                )}
                {tl(it.titulo, '') && (
                  <Titulo $size={c.tamano_titulo_tarjeta || '20px'} $color={c.color_titulo_tarjeta || '#1a2b4a'}>
                    {tl(it.titulo, '')}
                  </Titulo>
                )}
                {tl(it.texto, '') && (
                  <Texto $color={c.color_texto || '#808080'} dangerouslySetInnerHTML={{ __html: tl(it.texto, '') }} />
                )}
                {textoBoton && (
                  <Pie $align={c.alineacion_boton || 'center'}>
                    <Boton
                      type="button"
                      onClick={() => activar(it)}
                      $fondo={c.color_boton || 'linear-gradient(90deg, #c026d3 0%, #9333ea 65%, #7e22ce 100%)'}
                      $color={c.color_texto_boton || '#fff'}
                    >
                      {textoBoton}
                    </Boton>
                  </Pie>
                )}
              </Tarjeta>
            );
          })}
        </Grid>
      </Fila>
    </Seccion>
  );
};

export default TarjetasCursoSection;
